import React from 'react';
import { SEO } from '../components/seo/SEO';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Proof } from '../components/sections/Proof';
import { Contact } from '../components/sections/Contact';
import { Button } from '../components/ui/Button';
import {
    Zap, Shield, Lightbulb, Eye, TrendingUp,
    AlertTriangle, CheckCircle, Target, Users, Globe,
    Code, GitBranch, Layers, ArrowRight, Hammer, Rocket, Settings, ChevronRight
} from 'lucide-react';
import styles from './CompanyPage.module.css';



// Icon mapping for different list types
const getListIcon = (type: string, index: number) => {
    const iconProps = { size: 18, className: styles.listIcon };

    if (type === 'problems') {
        return <AlertTriangle {...iconProps} />;
    } else if (type === 'differentiation') {
        return <CheckCircle {...iconProps} />;
    } else if (type === 'principles') {
        const icons = [Code, GitBranch, Shield, Layers, Target];
        const Icon = icons[index % icons.length];
        return <Icon {...iconProps} />;
    } else if (type === 'team') {
        return <Users {...iconProps} />;
    } else if (type === 'partnership') {
        return <Target {...iconProps} />;
    }
    return <ArrowRight {...iconProps} />;
};

export const CompanyPage: React.FC = () => {
    const { t, language } = useLanguage();
    const {
        hero,
        story,
        reality,
        focus,
        differentiation,
        principles,
        team,
        dach,
        partnership,
        values
    } = t.companyPage;

    const valueIcons = [Zap, Shield, Lightbulb, Eye, TrendingUp];

    // Get enterprise partners from translations (same as homepage)
    const enterprisePartners = t.clientLogos?.enterpriseItems || [];

    return (
        <div className={styles.page}>
            <SEO
                title={hero.headline || hero.title}
                description={hero.subheadline || hero.subtitle}
                canonical="/company"
            />

            {/* Hero Section - Service Page Pattern */}
            <section className={styles.hero}>
                <div className={styles.hero__background} />
                <div className={styles.hero__grid} />
                <div className={styles.hero__overlay} />

                <motion.div
                    className={styles.hero__container}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <span className={styles.hero__badge}>
                        {language === 'en' ? 'About Us' : 'Über Uns'}
                    </span>
                    <h1 className={styles.hero__title}>
                        {hero.headline || hero.title}
                    </h1>
                    <p className={styles.hero__subtitle}>
                        {hero.subheadline || hero.subtitle}
                    </p>
                    {hero.intro && (
                        <p className={styles.hero__intro}>
                            {hero.intro}
                        </p>
                    )}
                    <div className={styles.hero__ctas}>
                        <Button variant="primary" size="lg" icon={<ArrowRight size={20} />} to="/consultation">
                            {language === 'en' ? 'Free Consultation' : 'Kostenlose Beratung'}
                        </Button>
                        <Button variant="secondary" size="lg" to="/contact">
                            {language === 'en' ? 'Get in Touch' : 'Kontakt aufnehmen'}
                        </Button>
                    </div>
                </motion.div>
            </section>

            {/* Story: Why Integrova AI was founded */}
            <section className={styles.section}>
                <div className={styles.container}>
                    <div className={styles.section__content}>
                        <h2>{story.title}</h2>
                        <p>{story.content}</p>
                        <div className={styles.problemsGrid}>
                            {story.problems.map((item: string, index: number) => {
                                const colorVariants = ['purple', 'blue', 'green', 'orange'];
                                const colorClass = styles[`problemCard--${colorVariants[index % 4]}`];
                                return (
                                    <div key={index} className={`${styles.problemCard} ${colorClass}`}>
                                        <div className={styles.problemCard__icon}>
                                            {getListIcon('problems', index)}
                                        </div>
                                        <p className={styles.problemCard__text}>
                                            {item}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* Reality check */}
            < section className={styles.section} >
                <div className={styles.container}>
                    <div className={styles.section__content}>
                        <h2>{reality.title}</h2>
                        <p>{reality.content}</p>
                        <div className={styles.problemsGrid}>
                            {reality.implications.map((item: string, index: number) => {
                                const colorVariants = ['purple', 'blue', 'green', 'orange'];
                                const colorClass = styles[`problemCard--${colorVariants[index % 4]}`];
                                return (
                                    <div key={index} className={`${styles.problemCard} ${colorClass}`}>
                                        <div className={styles.problemCard__icon}>
                                            {getListIcon('problems', index)}
                                        </div>
                                        <p className={styles.problemCard__text}>{item}</p>
                                    </div>
                                );
                            })}
                        </div>

                        <motion.div
                            className={styles.highlightBox}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <p>{focus.text}</p>
                        </motion.div>
                    </div>
                </div>
            </section >

            {/* Differentiation */}
            < section className={styles.section} >
                <div className={styles.container}>
                    <div className={styles.section__content}>
                        <h2>{differentiation.title}</h2>
                        <div className={styles.problemsGrid}>
                            {differentiation.items.map((item: string, index: number) => {
                                const colorVariants = ['green', 'blue', 'purple', 'orange'];
                                const colorClass = styles[`problemCard--${colorVariants[index % 4]}`];
                                return (
                                    <div key={index} className={`${styles.problemCard} ${colorClass}`}>
                                        <div className={styles.problemCard__icon}>
                                            {getListIcon('differentiation', index)}
                                        </div>
                                        <p className={styles.problemCard__text}>{item}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section >

            {/* Principles with Architecture Diagram */}
            < section className={styles.section} >
                <div className={styles.container}>
                    <div className={styles.sectionGrid}>
                        <div className={styles.section__content}>
                            <h2>{principles.title}</h2>
                            <div className={styles.problemsGrid}>
                                {principles.items.map((item: string, index: number) => {
                                    const colorVariants = ['blue', 'purple', 'green', 'orange'];
                                    const colorClass = styles[`problemCard--${colorVariants[index % 4]}`];
                                    return (
                                        <div key={index} className={`${styles.problemCard} ${colorClass}`}>
                                            <div className={styles.problemCard__icon}>
                                                {getListIcon('principles', index)}
                                            </div>
                                            <p className={styles.problemCard__text}>{item}</p>
                                        </div>
                                    );
                                })}
                            </div>
                            <p>{principles.goal}</p>
                        </div>

                        {/* Governance Architecture Visual - 3 Layer Model */}
                        <div className={styles.governanceVisual}>
                            {/* Layer 1: Responsibility (Authority) */}
                            <div className={styles.responsibilityLayer}>
                                <div className={styles.responsibilityNode}>
                                    <Shield size={20} className={styles.nodeIcon} />
                                    <div className={styles.nodeContent}>
                                        <span className={styles.nodeTitle}>Design & Architecture</span>
                                        <span className={styles.nodeSubtitle}>Intent & Accountability</span>
                                    </div>
                                </div>
                                <div className={styles.governanceConnector}></div>
                            </div>

                            {/* Layer 2: Execution Lifecycle (Linear Flow) */}
                            <div className={styles.executionLayer}>
                                <div className={styles.executionFlow}>
                                    {[
                                        { name: 'Build', icon: Hammer },
                                        { name: 'Deploy', icon: Rocket },
                                        { name: 'Operate', icon: Settings }
                                    ].map((phase, index) => (
                                        <React.Fragment key={phase.name}>
                                            <div className={styles.phaseNode}>
                                                <phase.icon size={16} className={styles.phaseIcon} />
                                                <span className={styles.phaseTitle}>{phase.name}</span>
                                            </div>
                                            {index < 2 && <div className={styles.phaseArrow}><ChevronRight size={20} /></div>}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>

                            {/* Layer 3: Outcome (Guaranteed Result) */}
                            <div className={styles.outcomeLayer}>
                                <div className={styles.evidenceBar}>
                                    <div className={styles.evidenceIndicator}>
                                        <CheckCircle size={16} />
                                        <span>Audit-Ready Evidence</span>
                                    </div>
                                    <div className={styles.evidenceDetails}>
                                        <span>Traceable</span>
                                        <div className={styles.dotSeparator}></div>
                                        <span>Logged</span>
                                        <div className={styles.dotSeparator}></div>
                                        <span>Reproducible</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section >

            {/* Team */}
            < section className={styles.section} >
                <div className={styles.container}>
                    <div className={styles.section__content}>
                        <h2>{team.title}</h2>
                        <p>{team.content}</p>
                        <div className={styles.problemsGrid}>
                            {team.workingStyle.map((item: string, index: number) => {
                                const colorVariants = ['purple', 'green', 'blue', 'orange'];
                                const colorClass = styles[`problemCard--${colorVariants[index % 4]}`];
                                return (
                                    <div key={index} className={`${styles.problemCard} ${colorClass}`}>
                                        <div className={styles.problemCard__icon}>
                                            {getListIcon('team', index)}
                                        </div>
                                        <p className={styles.problemCard__text}>{item}</p>
                                    </div>
                                );
                            })}
                        </div>
                        <p>{team.growth}</p>
                    </div>
                </div>
            </section >

            {/* DACH Focus - with distinct background */}
            < section className={`${styles.section} ${styles.sectionDach}`}>
                <div className={styles.container}>
                    <div className={styles.section__content}>
                        <div className={styles.dachHeader}>
                            <Globe size={32} className={styles.dachIcon} />
                            <h2>{dach.title}</h2>
                        </div>
                        {dach.content.map((paragraph: string, index: number) => (
                            <p key={index}>{paragraph}</p>
                        ))}
                        <p><strong>{dach.benefit}</strong></p>
                    </div>
                </div>
            </section >

            {/* Partners Hub - Marquee Style (Matching Homepage) */}
            < section className={styles.partnersSection} >
                <div className={styles.partnersContainer}>
                    <p className={styles.partnersHeader}>{t.clientLogos?.header || 'Technology Partners'}</p>

                    <div className={styles.partnersTrack}>
                        <div className={styles.partnersMarquee}>
                            {[...enterprisePartners, ...enterprisePartners].map((partner: { name: string; logo: string }, index: number) => (
                                <div key={`${partner.name}-${index}`} className={styles.partnerItem}>
                                    <img
                                        src={partner.logo}
                                        alt={partner.name}
                                        className={styles.partnerImage}
                                        width="120"
                                        height="40"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section >

            {/* Proof Section - Metrics */}
            < Proof />

            {/* Values - Isometric Dashboard Cards */}
            < section className={styles.valuesSection} >
                <div className={styles.container}>
                    <h2 className={styles.valuesTitle}>{values.title}</h2>
                    <div className={styles.valuesGrid}>
                        {values.items.map((item: { name: string; subline: string }, index: number) => {
                            const Icon = valueIcons[index % valueIcons.length];
                            return (
                                <motion.div
                                    key={index}
                                    className={styles.valueCard}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-50px' }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                >
                                    <div className={styles.valueIcon}>
                                        <Icon size={32} strokeWidth={1.5} />
                                    </div>
                                    <h3 className={styles.valueName}>{item.name}</h3>
                                    <p className={styles.valueSubline}>{item.subline}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section >

            {/* Partnership */}
            < section className={styles.section} >
                <div className={styles.container}>
                    <div className={styles.section__content}>
                        <h2>{partnership.title}</h2>
                        <p>{partnership.text}</p>
                        <div className={styles.problemsGrid}>
                            {partnership.roles.map((item: string, index: number) => {
                                const colorVariants = ['orange', 'blue', 'green', 'purple'];
                                const colorClass = styles[`problemCard--${colorVariants[index % 4]}`];
                                return (
                                    <div key={index} className={`${styles.problemCard} ${colorClass}`}>
                                        <div className={styles.problemCard__icon}>
                                            {getListIcon('partnership', index)}
                                        </div>
                                        <p className={styles.problemCard__text}>{item}</p>
                                    </div>
                                );
                            })}
                        </div>
                        <motion.div
                            className={styles.highlightBox}
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                        >
                            <p>{partnership.summary}</p>
                        </motion.div>
                    </div>
                </div>
            </section >

            {/* Contact Section */}
            < Contact />
        </div >
    );
};
